<script lang="ts" setup>
import { canRankFeatures } from '@beeboard/common';
import {
  defaultOptions,
  PrimeVueLocaleOptions,
  usePrimeVue,
} from 'primevue/config';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/authStore';
import { useProfileStore } from '@/store/profileStore';
import { useUiStore } from '@/store/uiStore';
import { setLocale } from '@/utils/i18n/vueI18n';
import type { DropdownOption } from '@/ui/AppDropdown.vue';

type Mode = 'minimal' | 'full';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LocaleFile = Record<string, any>;

const router = useRouter();
const profileStore = useProfileStore();
const authStore = useAuthStore();
const uiStore = useUiStore();
const primevue = usePrimeVue();
const { locale } = useI18n();

const props = defineProps<{
  mode: Mode;
}>();

const isFull = computed(() => {
  return props.mode === 'full';
});

const dropdownOptions = computed<DropdownOption[]>(() => {
  const options: DropdownOption[] = [
    {
      label: $t('sideNavigation.userProfile'),
      value: 'userProfile',
    },
    {
      label: $t('sideNavigation.userSettings'),
      value: 'userSettings',
    },
  ];

  if (canRankFeatures(profileStore.profile.roles)) {
    options.push({
      label: $t('sideNavigation.featureRanking'),
      value: 'featureRanking',
    });
  }

  options.push(
    {
      label: $t('sideNavigation.languages'),
      value: '',
      icon: 'Globe',
      disabled: true,
    },
    {
      label: $t('sideNavigation.german'),
      value: 'de',
    },
    {
      label: $t('sideNavigation.english'),
      value: 'en',
    },
    {
      label: $t('sideNavigation.logout'),
      value: 'logout',
      icon: 'LogOut',
    },
  );

  return options;
});

async function select(value: string) {
  if (value === 'userProfile') {
    await router.push({ name: 'UserMePage' });

    return;
  }
  if (value === 'userSettings') {
    await router.push({ name: 'UserSettingsPage' });

    return;
  }
  if (value === 'featureRanking') {
    await router.push({ name: 'FeatureRanking' });

    return;
  }

  if (value === 'logout') {
    if (uiStore.pendingOperation) {
      if (uiStore.isContextFormLoading) {
        useAlerts().warning($t('message.savingChanges'));

        return;
      } else {
        const confirmed = await uiStore.confirmUnlockUi();
        if (!confirmed) {
          return;
        }
      }
    }
    await authStore.logout();

    return;
  }

  locale.value = value;
  setLocale(value);

  await updatePrimeVueConfigWithLocaleTag(locale.value);
}

async function updatePrimeVueConfigWithLocaleTag(localeTag: string) {
  if (!localeTag) {
    return;
  }

  const primeVueLocaleFiles: Record<string, () => Promise<LocaleFile>> =
    import.meta.glob('/node_modules/primelocale/*.json');

  const localeRe = new RegExp(`\\b${localeTag}.json$`, 'i');
  const langRe = new RegExp(`\\b${localeTag.split('-')[0]}.json$`, 'i');
  let localeFileName = Object.keys(primeVueLocaleFiles).find((locale) =>
    localeRe.test(locale),
  );

  localeFileName ??= Object.keys(primeVueLocaleFiles).find((locale) =>
    langRe.test(locale),
  );

  if (!localeFileName) {
    console.error(`Locale file for ${localeTag} not found.`);

    return;
  }
  const localeFile =
    Object.values(
      (await primeVueLocaleFiles[localeFileName]?.())?.default,
    )[0] ?? defaultOptions.locale;

  if (!primevue.config.locale) {
    console.error(`PrimeVueConfiguration is undefined.`);

    return;
  }

  primevue.config.locale = {
    ...primevue.config.locale,
    ...(localeFile as PrimeVueLocaleOptions),
  };
}

onMounted(() => {
  const tasks: Promise<unknown>[] = [
    updatePrimeVueConfigWithLocaleTag(locale.value),
  ];
  if (!profileStore.profile.email) {
    tasks.push(profileStore.loadProfile());
  }
  if (!profileStore.settings.user_id) {
    tasks.push(profileStore.loadUserSettings());
  }

  return Promise.all(tasks).catch(useAlerts().error);
});

const user = computed(() => {
  return profileStore.profile;
});

const userInitials = computed(() => {
  return user.value?.email.charAt(0).toUpperCase() || '';
});
</script>

<template>
  <div class="app-side-navigation-user" :data-mode="mode">
    <div v-if="!isFull" class="avatar">
      <AppDropdown
        :options="dropdownOptions"
        :current-option="locale"
        @input="select"
      >
        <UiAvatar :initials="userInitials" :image-url="profileStore.image" />
      </AppDropdown>
    </div>

    <div class="info">
      <UiAvatar
        v-show="isFull"
        :initials="userInitials"
        :image-url="profileStore.image"
      />
      <Transition name="fade">
        <div v-if="isFull" class="info-body">
          <div class="data">
            <div class="name">
              {{ user?.first_name }}
              {{ user?.last_name }}
            </div>
            <div class="email">{{ user?.email }}</div>
          </div>

          <div class="menu">
            <AppDropdown
              :options="dropdownOptions"
              :current-option="locale"
              @input="select"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-side-navigation-user {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: stretch;
}

.avatar {
  display: flex;
  z-index: 1;
}

.info {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: var(--space-16);
}

.info-body {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: var(--space-16);

  &.fade-enter-active,
  &.fade-leave-active {
    transition: all var(--duration-base) ease-out;
  }

  &.fade-enter-from,
  &.fade-leave-to {
    opacity: 0;
    transform: translateX(#{px(-24)});
  }
}

.menu {
  flex: 0 0 auto;
}

.data {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: px(296-124);
  width: 20px;
  &:hover {
    overflow: visible;
  }
}

.name {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-14);
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    overflow: visible;
  }
}

.email {
  color: var(--color-gray-500);
  font-size: var(--font-size-14);
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    overflow: visible;
  }
}
</style>
